import { type CSSProperties } from 'react';
import IconFlash from '@/components/icons/IconFlash';
import IconNote from '@/components/icons/IconNote';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { IndexItem } from '@/features/webinar/types';
import { getFontFamily } from '@/lib/utils';
import { Clock, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const accordionData = [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }];

export default function PreviewWaitingRoom({
  visualIdentity,
  theme,
  accentColor,
  backgroundColor,
  contrastColor,
  backgroundImage,
  showSpeakers,
  showIndex,
  indexItems,
  previewVisualIdentity,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  theme: 'LIGHT' | 'DARK';
  accentColor: string;
  backgroundColor: string;
  contrastColor: string;
  backgroundImage?: string;
  showSpeakers?: boolean;
  showIndex?: boolean;
  indexItems?: IndexItem[];
  previewVisualIdentity?: boolean;
}) {
  const { t } = useTranslation('webinars');
  const containerStyle: CSSProperties = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background: backgroundColor,
      };

  return (
    <div
      className="p-4 w-full rounded-[20px] relative flex items-center justify-center"
      style={containerStyle}
    >
      <div className=" w-full flex items-center justify-center gap-6   ">
        {/* left side */}
        <div className="w-1/2  flex flex-col gap-[10px]">
          {visualIdentity?.logoUrl && (
            <img
              src={visualIdentity?.logoUrl}
              alt="logo"
              width={140 * 0.35}
              height={64 * 0.35}
              className="object-contain"
            />
          )}

          <h1
            className={`text-[32px * 0.35]  font-semibold ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
            style={{
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('waitingRoom.publicPage.greeting')}{' '}
            <span
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                color: accentColor,
              }}
            >
              John Doe
              {t('waitingRoom.publicPage.greetingEnd')}
            </span>
          </h1>

          <div className="flex flex-col gap-4 items-center justify-center">
            <h2
              className={`text-[24px * 0.35] font-bold ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              Lorem ipsum dolor sit amet consectetur adipisicing elit.
            </h2>
            <p
              className={`text-[6px] font-normal ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos. Lorem ipsum
              dolor sit amet consectetur adipisicing elit.
            </p>

            {showSpeakers && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <img src="/assets/avatar.svg" alt="avatar" width={16} height={16} />
                  <div>
                    <p
                      className={`text-[6px] ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                      style={{
                        fontFamily: getFontFamily(visualIdentity?.fontFamily),
                      }}
                    >
                      {t('waitingRoom.publicPage.presenterBy')}
                      <span
                        className=" text-[6px] font-semibold"
                        style={{
                          fontFamily: getFontFamily(visualIdentity?.fontFamily),
                        }}
                      >
                        John Doe
                      </span>{' '}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {previewVisualIdentity && (
              <div className="w-full">
                <p
                  className={`text-[6px] pb-4 text-left ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                  style={{
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  }}
                >
                  {t('waitingRoom.indexTitle')}
                </p>
                <div className="flex flex-col gap-2">
                  {accordionData.map((item, index) => (
                    <div
                      key={item.id}
                      className="bg-[#FBFBFB] rounded-[20px] px-4 py-2 flex items-center justify-between "
                      style={{
                        backgroundColor:
                          theme === 'LIGHT'
                            ? 'rgba(31, 31, 31, 0.03)'
                            : theme === 'DARK'
                              ? 'rgba(255, 255, 255, 0.1)'
                              : undefined,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[6px]"
                          style={{
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            color: accentColor,
                          }}
                        >
                          {index + 1}.
                        </span>{' '}
                        <span
                          className="text-[6px]"
                          style={{
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            color: theme === 'DARK' ? 'white' : 'black',
                          }}
                        >
                          {t('waitingRoom.publicPage.indexTitle')}
                        </span>
                      </div>
                      <Plus
                        className="size-3 shrink-0 transition-transform duration-200 text-highlighted"
                        style={{ color: accentColor }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showIndex && indexItems && indexItems.length > 0 && (
              <div className="w-full">
                <p
                  className={`text-[6px] pb-4 text-left ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                  style={{
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  }}
                >
                  {t('waitingRoom.indexTitle')}
                </p>
                <div className="flex flex-col gap-2">
                  {indexItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-[#FBFBFB] rounded-[20px] px-4 py-2 flex items-center justify-between "
                      style={{
                        backgroundColor:
                          theme === 'LIGHT'
                            ? 'rgba(31, 31, 31, 0.03)'
                            : theme === 'DARK'
                              ? 'rgba(255, 255, 255, 0.1)'
                              : undefined,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[6px]"
                          style={{
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            color: accentColor,
                          }}
                        >
                          {item.position + 1}.
                        </span>{' '}
                        <span
                          className="text-[6px]"
                          style={{
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            color: theme === 'DARK' ? 'white' : 'black',
                          }}
                        >
                          {item.title}
                        </span>
                      </div>
                      {item.description && item.description.trim().length > 0 && (
                        <Plus
                          className="size-3 shrink-0 transition-transform duration-200 text-highlighted"
                          style={{ color: accentColor }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* right side */}
        <div className="w-1/2  flex flex-col gap-[10px] items-center">
          {/* logo welya */}
          <div className="flex items-center gap-2 justify-end w-full">
            <p
              className="text-white font-medium text-[6px] "
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                color: theme === 'LIGHT' ? '#000000' : '#FFFFFF',
              }}
            >
              powered by
            </p>
            {theme === 'LIGHT' && (
              <img src="/assets/logo-black.svg" alt="logo" width={30} height={30} />
            )}
            {theme === 'DARK' && (
              <img src="/assets/logo-white.svg" alt="logo" width={30} height={30} />
            )}
          </div>

          <div
            className="w-full flex flex-col items-center  gap-2 p-1 rounded-2xl border "
            style={{
              backgroundColor:
                theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',

              borderColor: contrastColor,
            }}
          >
            <div className="flex items-center gap-0.5">
              <Clock className="size-3 text-[#9E9FA6] mr-2" />
              <div
                className={`text-[6px] font-semibold mr-2 ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                }}
              >
                10:00 Europe/Paris
              </div>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div
                className="text-[10px] font-semibold"
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  color: accentColor,
                }}
              >
                00:23:46
              </div>
              <p
                className={`text-[6px]`}
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  color: theme === 'DARK' ? '#929185' : '#6D6E7A',
                }}
              >
                {t('waitingRoom.publicPage.countdown.remainingShort')}
              </p>
            </div>

            <p
              className="text-[6px] text-center"
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                color: theme === 'LIGHT' ? '#17805C' : '#C0FFEA',
              }}
            >
              <span className="text-[6px] font-medium">1218</span>{' '}
              {t('waitingRoom.publicPage.countdown.attendees', { count: 1218 })}
            </p>
          </div>

          {/* btn */}
          <div className={` w-full grid  gap-6  grid-cols-3 `}>
            <div
              className="flex items-center gap-2 p-1 rounded-lg"
              style={{
                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <div>
                <div className="size-4 rounded-lg bg-black flex items-center justify-center">
                  <img src="/assets/connexion.svg" alt="connexion" width={12} height={12} />
                </div>
              </div>

              <div className="flex flex-col">
                <p
                  className="text-[6px]"
                  style={{
                    color: theme === 'DARK' ? 'white' : 'black',
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  }}
                >
                  {t('waitingRoom.publicPage.connection.label')}
                </p>
                <p
                  className="text-[6px]"
                  style={{
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                    color: theme === 'DARK' ? 'white' : 'black',
                  }}
                >
                  {t('waitingRoom.publicPage.connection.strong')}
                </p>
              </div>
            </div>

            <div
              className="flex items-center gap-2 p-1 rounded-lg"
              style={{
                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <div>
                <div className="size-4 rounded-lg bg-black flex items-center justify-center">
                  <IconFlash color={contrastColor} size={12} />
                </div>
              </div>
              <p
                className="text-[6px]"
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  color: theme === 'DARK' ? 'white' : 'black',
                }}
              >
                {t('waitingRoom.survey.exploreTool')}
              </p>
            </div>

            <div
              className="flex items-center gap-2 p-1 rounded-lg"
              style={{
                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <div>
                <div className="size-4 rounded-lg bg-black flex items-center justify-center">
                  <IconNote color={contrastColor} size={12} />
                </div>
              </div>
              <p
                className="text-[6px]"
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  color: theme === 'DARK' ? 'white' : 'black',
                }}
              >
                {t('waitingRoom.publicPage.survey.cta')}
              </p>
            </div>
          </div>

          <div
            className={`w-full  rounded-[20px] `}
            style={{
              borderRadius: '20px',

              backgroundColor:
                theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
            }}
          >
            <img
              src="/assets/waiting-room.png"
              alt="waiting-room-bg"
              className="w-full h-full object-cover rounded-[20px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
